import Tooltip from "../common/Tooltip";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { db } from "../../firebase/config";
import describeQueryError from "../../helpers/firestore/describeQueryError";
import QuestionMeta from "../dashboard/QuestionMeta";
import LiveReportRenderContext from "../../StateManagment/LiveReportRenderContext";
import { useFirestoreNoCompagny } from "../../firebase/useFirestore";
import Modal from 'react-modal';
import Location from '../../hooks/Location';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DataType from './DataType';
import useChartColors from '../../hooks/useChartColors';
import fetchMeasureMomentsForResearches from '../../helpers/dashboardHelpers/fetchMeasureMomentsForResearches';
import WeeklyResultsChart from '../common/WeeklyResultsChart';
import TemplateIndicatorChart from '../liveReport/reportBuilder2/widgets/templateRenderers/TemplateIndicatorChart';
import CardHeaderInfoPopover from '../common/CardHeaderInfoPopover';
import describeShowWhen from '../../helpers/questionnaire/describeShowWhen';
import useShowWhenLabels from '../../hooks/useShowWhenLabels';
import ProjectsBreakdownSection from '../common/ProjectsBreakdownSection';
import ResponsesSummarySection from '../common/ResponsesSummarySection';
import DeepAnalysisSlotsContext from '../../StateManagment/DeepAnalysisSlotsContext';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import useHierarchyData from '../../hooks/useHierarchyData';
import defaultDates from '../../utils/defaultDates';
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import summarizeRespondentsByMoment from '../../helpers/dataHelpers/summarizeRespondentsByMoment';
import momentTitlesForRecords from '../../helpers/dataHelpers/momentTitlesForRecords';
import summarizeRespondentOverlap from '../../helpers/dataHelpers/summarizeRespondentOverlap';
import RespondentOverlapNote from '../common/RespondentOverlapNote';
import detectSuspectSubmissions from '../../helpers/analysisHelpers/detectSuspectSubmissions';
import SuspectSubmissionsNote from '../common/SuspectSubmissionsNote';
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext';

// Conditional onSnapshot — skips the subscription entirely when `enabled`
// is false. Avoids idle listeners when the override branch owns the data.
const useConditionalNoCompagny = (enabled, collection, field, id) => {
    const [docs, setDocs] = useState([])
    useEffect(() => {
        if (!enabled || !id) {
            setDocs([])
            return
        }
        const unsub = db.collection(collection)
            .where(field, '==', id)
            .onSnapshot((snap) => {
                const next = []
                snap.forEach((d) => next.push({ ...d.data(), docid: d.id }))
                setDocs(next)
            }, (error) => console.error(
                describeQueryError(collection, [[field, '==', id]], error), error
            ))
        return () => unsub()
    }, [enabled, collection, field, id])
    return docs
}

/**
 * @param {object} props
 * @param {string} props.effectId - ID of the Effect this indicator belongs to.
 * @param {string} [props.effectTitle] - Effect title, shown in the info popover header.
 * @param {string} [props.effectDescription] - Effect description, shown in the info popover header.
 * @param {import("../../types/Firebase").QuestionnaireField} props.indicator - Data from QuestionnaireFields collection.
 * @param {string} [props.startDate] - Start of the date range filter.
 * @param {string} [props.endDate] - End of the date range filter.
 * @param {string} props.docid - Firestore doc id of the QuestionnaireFields document.
 * @param {string} props.graphType - Chart type: 'bar' | 'line' | 'pie'.
 * @param {string} props.personaId - Persona filter; '' or null for no filter.
 * @param {'graph' | 'text' | 'both'} props.paragrapgMomentVisibilityStyle - Visibility style for paragraph-moment questions.
 * @param {Object<string, string>} props.colors - Per-item color map passed to DataType.
 * @param {Array<string>} props.colorPalette - Chart color palette.
 * @param {Object<string, string>} props.colorOverrides - Card-level color overrides.
 * @param {string} [props.numberFontSize] - CSS font-size for the total-growth number view.
 * @param {'left'|'center'|'right'} [props.numberTextAlign] - Alignment for the total-growth number view.
 * @param {string} [props.numberFontWeight] - Font weight for the total-growth number view.
 * @param {React.ReactNode} props.dashboardOptions - Pre-rendered options footer.
 * @param {Array<string>} [props.researchIdsOverride] - Targetgroup-derived research-id restriction. When an array, the override path is active.
 * @param {boolean} [props.filterByResearchId] - Opt-in research-id filtering for the hierarchy fetch.
 * @param {object} [props.templateData] - Template-mode data; when set, renders TemplateIndicatorChart instead of live data.
 * @returns
 */
const QuestionData = ({
    effectId,
    effectTitle,
    effectDescription,
    indicator,
    startDate,
    endDate,
    docid,
    graphType,
    graphColor,
    personaId,
    paragrapgMomentVisibilityStyle,
    colors,
    colorPalette,
    colorOverrides,
    numberFontSize,
    numberTextAlign,
    numberFontWeight,
    dashboardOptions,
    researchIdsOverride,
    filterByResearchId,
    templateData,
}) => {

    // Buiten een live rapportage komt de kleur uit het vaste palet, dus de
    // primaire kaartkleur die hier ooit gekozen is telt niet meer mee.
    const { overrides } = useChartColors({ colorPalette, colorOverrides });

    const inLiveReport = useContext(LiveReportRenderContext);
    // Deep-analysis redesign: when the page provides slots, the popover
    // content is split and portalled into page-level sections instead —
    // facts into the context card, the weekly chart + totals into the
    // "Deelname" section. Same data, different place.
    const deepAnalysisSlots = useContext(DeepAnalysisSlotsContext);

    // State
    const [researchId, setResearchId] = useState('');
    const [numberOfMeasureMoments, setNumberOfMeasureMoments] = useState(0);
    const [type, setType] = useState('');
    const [momentId, setMomentId] = useState('');
    const [showResponsesVisibilityOptions, setShowResponsesVisibilityOptions] = useState(false)
    const [overrideMeasureMoments, setOverrideMeasureMoments] = useState(null)
    const [projectBreakdown, setProjectBreakdown] = useState(/** @type {Object<string, string> | null} */ (null))

    // Reset per-project averages when the indicator changes — only Scale/Number
    // children repopulate this via onProjectBreakdownChange, so without a reset
    // a Multiple/Open indicator would inherit the previous indicator's averages.
    useEffect(() => {
        setProjectBreakdown(null)
    }, [indicator?.ID])

    // Hooks
    const currentPage = Location()[2];
    Modal.setAppElement('#root');

    const overrideActive = Array.isArray(researchIdsOverride)

    // Firestore — legacy path (single research resolved from questionnaire ID).
    // Both listeners are gated off when override is active so we don't hold
    // idle onSnapshot subscriptions.
    const research = useConditionalNoCompagny(!overrideActive, 'Research', 'QuestionnaireID', indicator.QuestionnaireID || '')
    const legacyMeasureMoments = useConditionalNoCompagny(!overrideActive, 'MeasureMoments', 'ResearchID', researchId || '')
    const matrixQuestionRows = useFirestoreNoCompagny('MatrixQuestionRows', 'Field', indicator.ID ? indicator.ID : '')

    // Conditionele vraag: deze grafiek gaat niet over alle respondenten, maar
    // over wie de vraag kreeg. Zonder die regel eronder leest een lage n als
    // non-respons in plaats van als niet-van-toepassing.
    //
    // De opzoeking van de labels zit in `useShowWhenLabels` — zelfde recept als
    // op het printvel. In template-modus wordt er niets opgehaald: die render is
    // cross-tenant en zou leeg terugkomen.
    // AI-CONTEXT: See docs/pitfalls.md#state-firestore-lookup-empty-in-template-mode
    // AI-CONTEXT: See docs/features/conditional-questions.md
    const { triggerField, labels: showWhenLabels } = useShowWhenLabels(indicator, { enabled: !templateData })

    // Zelfde zin als de chip in de vragenlijstbouwer — één bron, zodat de twee
    // niet uiteen kunnen lopen.
    const showWhenNote = useMemo(() => {
        if (!triggerField) return ''
        const described = describeShowWhen(indicator, [triggerField], showWhenLabels)
        return described.state === 'ok' ? described.condition : ''
    }, [triggerField, indicator, showWhenLabels])
    const questionnaires = useFirestoreNoCompagny('Questionnaires', 'ID', indicator.QuestionnaireID || '')

    // Get research id (old path only)
    useEffect(() => {
        if (overrideActive) return;
        research && research.map(doc => {
            setResearchId(doc.ID);
        });
    }, [research, overrideActive]);

    // Override path: one-shot fetch of MeasureMoments for all paired researches.
    // researchIdsOverride is memoised upstream (EffectCardContent), so its
    // reference only changes when the user actually picks a different targetgroup.
    useEffect(() => {
        if (!overrideActive) {
            setOverrideMeasureMoments(null)
            return
        }
        if (researchIdsOverride.length === 0) {
            setOverrideMeasureMoments([])
            return
        }
        let cancelled = false
        fetchMeasureMomentsForResearches(researchIdsOverride).then((mm) => {
            if (!cancelled) setOverrideMeasureMoments(mm)
        })
        return () => { cancelled = true }
    }, [overrideActive, researchIdsOverride]);

    // Soft-deleted moments (ADR-008) are hidden on both paths. Keep null as-is:
    // downstream effects distinguish "not loaded yet" (null) from "empty" ([]).
    const measureMomentsRaw = overrideActive ? overrideMeasureMoments : legacyMeasureMoments;
    const measureMoments = useMemo(
        () => (Array.isArray(measureMomentsRaw) ? measureMomentsRaw.filter((m) => !m.Deleted) : measureMomentsRaw),
        [measureMomentsRaw]
    );

    // Get number of measure moments
    useEffect(() => {
        if (Array.isArray(measureMoments)) {
            setNumberOfMeasureMoments(measureMoments.length);
        }
    }, [measureMoments]);

    // Get type of moment
    useEffect(() => {
        if (numberOfMeasureMoments === 1) {
            setType(`${indicator.Type}-moment`);
            setMomentId(measureMoments[0].ID);
        } else if (numberOfMeasureMoments > 1) {
            setType(`${indicator.Type}-development`);
        } else {
            setType('output');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberOfMeasureMoments]);

    // Value passed to DataType: array when override is active, single string otherwise
    const researchIdForDataType = overrideActive ? researchIdsOverride : researchId;

    // Popover info for the MSI-matrix branch — same shape as IndicatorData's modal.
    const isMsiMatrix = (indicator.Type === 'matrix-one' || indicator.Type === 'matrix-multiple')
        && indicator.Standard === 'true';

    const researchTitle = (research && research[0] && research[0].Title) || '—';
    const meetmomentLabel = Array.isArray(measureMoments) && measureMoments.length === 1
        ? (measureMoments[0].Title || '—')
        : (Array.isArray(measureMoments) && measureMoments.length > 1
            ? `${measureMoments.length} meetmomenten`
            : '—');
    const questionnaireTitle = (questionnaires && questionnaires[0] && questionnaires[0].Title) || '—';
    const hasResearch = researchTitle !== '—';

    // Response records for the popover / deep-analysis sections. Mirrors the
    // analytics-branch implementation: fetched on every tenant (the hierarchy
    // is just the tenant itself on project tenants) so respondent counts come
    // from one source everywhere.
    const { defaultStartDate, defaultEndDate } = useMemo(() => defaultDates(), []);
    const popoverStartMs = useMemo(
        () => toMillis(startDate) ?? toMillis(defaultStartDate) ?? 0,
        [startDate, defaultStartDate]
    );
    const popoverEndMs = useMemo(
        () => toMillis(endDate) ?? toMillis(defaultEndDate) ?? 8640000000000000,
        [endDate, defaultEndDate]
    );
    const popoverResearchIds = overrideActive
        ? researchIdsOverride
        : (researchId ? [researchId] : []);
    const {
        records: popoverRecords,
        respondentsPerProject: popoverRespondents,
        loading: hierarchyLoading,
        error: hierarchyError,
    } = useHierarchyData({
        syncType: isMsiMatrix ? 'MSIEffect' : 'Question',
        id: indicator.ID,
        startDate: popoverStartMs,
        endDate: popoverEndMs,
        researchIds: popoverResearchIds,
        personaId,
        filterByResearchId,
    });

    // Position → title / id → position maps for the per-moment breakdown.
    // First title per position wins — on portfolio tenants several synced
    // researches share positions (mirrors the analytics-branch version).
    const { id2pos, pos2title } = useMemo(() => {
        const id2posMap = {};
        const pos2titleMap = {};
        (Array.isArray(measureMoments) ? measureMoments : []).forEach((m) => {
            const position = typeof m.Position === 'number' ? m.Position : parseInt(m.Position, 10);
            if (Number.isNaN(position)) return;
            if (m.ID) id2posMap[m.ID] = position;
            if (m?.Title && pos2titleMap[position] === undefined) pos2titleMap[position] = m.Title;
        });
        // Meerdere onderzoeken → alleen posities; zie momentTitlesForRecords.
        return { id2pos: id2posMap, pos2title: momentTitlesForRecords(pos2titleMap, popoverRecords) };
    }, [measureMoments, popoverRecords]);

    // Respondents + answers (per moment when >1) from the hierarchy records
    // (includes synced child projects on portfolio tenants).
    const responseSummary = useMemo(
        () => summarizeRespondentsByMoment(popoverRecords, { id2pos, pos2title }),
        [popoverRecords, id2pos, pos2title]
    );

    // Who the growth figure actually rests on. Deduped by Persona (a PERSON);
    // responseSummary above dedupes by FormID, which is one SUBMISSION and can
    // never match across two moments. Null with fewer than two moments.
    // `useHierarchyData` houdt de VORIGE records vast zolang een nieuwe fetch
    // loopt. Zonder deze poort staan de deelnemers en de signaleringen van de
    // vorige vraag onder de kop van de nieuwe — met de harde zin "geen enkele
    // deelnemer komt in beide metingen voor" erbij. `null` betekent hier 'nog
    // niet bekend', en dat is iets anders dan 'niets gevonden'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    // Ook gepoort op het slot: buiten de diepte-analyse worden beide uitkomsten
    // weggegooid, en dit component staat op elke dashboardkaart.
    const notitiesZichtbaar = Boolean(deepAnalysisSlots?.participationEl)
        && !hierarchyLoading && !hierarchyError;

    const respondentOverlap = useMemo(
        () => (notitiesZichtbaar
            ? summarizeRespondentOverlap(popoverRecords, { id2pos, pos2title })
            : null),
        [notitiesZichtbaar, popoverRecords, id2pos, pos2title]
    );

    // Invullingen die het gemiddelde vertekenen. Puur over de records die er al
    // zijn; `matrixQuestionRows` levert de valentie (nooit afleiden uit ruw vs
    // gespiegeld — midden op de schaal zijn die identiek). Null zolang er nog
    // niets binnen is, zodat het scherm geen afwezigheid beweert.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const suspectSubmissions = useMemo(() => {
        if (!notitiesZichtbaar) return null;
        if (!Array.isArray(popoverRecords) || popoverRecords.length === 0) return null;
        return detectSuspectSubmissions({
            records: popoverRecords,
            rows: matrixQuestionRows,
            field: indicator,
            id2pos,
            pos2title,
        });
    }, [notitiesZichtbaar, popoverRecords, matrixQuestionRows, indicator, id2pos, pos2title]);

    // Decca krijgt de aantallen mee (besluit 2026-09-22), zodat hij een slag om
    // de arm kan houden in plaats van het gemiddelde zelfverzekerd te duiden.
    // Alleen op Deccos Pro: zonder Pro is de provider null en publiceert niemand.
    const aiContext = useContext(DeepAnalysisAIContext);
    const publishToAI = aiContext?.publish;
    // Bij het bladeren naar een andere vraag blijft dit component gemonteerd.
    // Zonder een expliciete `null` blijven de cijfers van de vórige vraag in
    // Decca's context staan, en presenteert hij die als die van deze vraag.
    // AI-CONTEXT: See docs/pitfalls.md#state-stale-derived-state-on-item-cycler-prop-change
    useEffect(() => {
        if (!publishToAI) return;
        if (!suspectSubmissions) {
            publishToAI('verdachteInvullingen', null);
            return;
        }
        const { notRead, controlMismatch, outliers, coverage } = suspectSubmissions;
        publishToAI('verdachteInvullingen', {
            invullingenGetoetst: coverage.checked,
            lijktNietGelezen: notRead.length,
            controlevraagWijktAf: controlMismatch.length,
            uitschieters: outliers.length,
        });
    }, [publishToAI, suspectSubmissions]);

    // Info rows for the popover — effect header, measurement rows, weekly chart.
    const popoverInfoRows = (
        <>
            {(effectTitle || effectDescription) && (
                <div className="info-popover-effect-header">
                    {effectTitle && <p className="info-popover-effect-title">{effectTitle}</p>}
                    {effectDescription && <p className="info-popover-effect-description">{effectDescription}</p>}
                </div>
            )}
            <div className="info-popover-row">
                <span className="info-popover-row-label">Onderzoek</span>
                <span className="info-popover-row-value">{researchTitle}</span>
            </div>
            <div className="info-popover-row">
                <span className="info-popover-row-label">Meetmoment</span>
                <span className="info-popover-row-value">{meetmomentLabel}</span>
            </div>
            {hasResearch && (
                <div className="info-popover-row">
                    <span className="info-popover-row-label">Vragenlijst</span>
                    <span className="info-popover-row-value">{questionnaireTitle}</span>
                </div>
            )}
            <ResponsesSummarySection
                respondents={responseSummary.totalRespondents}
                answers={responseSummary.totalAnswers}
                perMoment={responseSummary.perMoment}
                loading={hierarchyLoading}
                error={hierarchyError}
            />
            <WeeklyResultsChart
                records={popoverRecords}
                title="Antwoorden per week"
                dedupeKey="FormID"
            />
        </>
    );

    // Popover content — info rows plus the per-project breakdown (portfolio only).
    const popoverContent = (
        <div>
            {popoverInfoRows}
            {projectBreakdown
                ? <ProjectsBreakdownSection values={projectBreakdown} valueLabel="Gemiddelde" average />
                : <ProjectsBreakdownSection values={popoverRespondents} valueLabel="Antwoorden" />}
        </div>
    );

    const factsContent = (
        <>
            <div className="deep-analysis-fact">
                <span className="deep-analysis-fact-k"><GridOnOutlinedIcon /> Onderzoek</span>
                <span className="deep-analysis-fact-v">{researchTitle}</span>
            </div>
            <div className="deep-analysis-fact">
                <span className="deep-analysis-fact-k"><ScheduleOutlinedIcon /> Meetmoment</span>
                <span className="deep-analysis-fact-v">{meetmomentLabel}</span>
            </div>
            {hasResearch && (
                <div className="deep-analysis-fact">
                    <span className="deep-analysis-fact-k"><FactCheckOutlinedIcon /> Vragenlijst</span>
                    <span className="deep-analysis-fact-v">{questionnaireTitle}</span>
                </div>
            )}
            <div className="deep-analysis-fact">
                <span className="deep-analysis-fact-k"><GroupsOutlinedIcon /> Respondenten</span>
                <span className="deep-analysis-fact-v deep-analysis-fact-v--num">{responseSummary.totalRespondents}</span>
            </div>
            <div className="deep-analysis-fact">
                <span className="deep-analysis-fact-k"><ChatOutlinedIcon /> Antwoorden</span>
                <span className="deep-analysis-fact-v deep-analysis-fact-v--num">{responseSummary.totalAnswers}</span>
            </div>
        </>
    );

    const participationContent = (
        <>
            <div className="deep-analysis-participation">
                <div className="deep-analysis-participation-chart">
                    <WeeklyResultsChart
                        records={popoverRecords}
                        dedupeKey="FormID"
                    />
                </div>
                <div className="deep-analysis-participation-totals">
                    <div className="deep-analysis-bigstat">
                        <div className="deep-analysis-bigstat-n">{responseSummary.totalAnswers}</div>
                        <div className="deep-analysis-bigstat-l">antwoorden totaal</div>
                    </div>
                    <div className="deep-analysis-bigstat">
                        <div className="deep-analysis-bigstat-n">{numberOfMeasureMoments}</div>
                        <div className="deep-analysis-bigstat-l">meetmomenten</div>
                    </div>
                    <div className="deep-analysis-bigstat">
                        <div className="deep-analysis-bigstat-n">{responseSummary.totalRespondents}</div>
                        <div className="deep-analysis-bigstat-l">unieke respondenten</div>
                    </div>
                </div>
            </div>
            {/* Per-moment breakdown table — only when there is more than one
                moment; with a single moment it would repeat the totals above. */}
            {responseSummary.perMoment.length > 1 && (
                <ResponsesSummarySection
                    respondents={responseSummary.totalRespondents}
                    answers={responseSummary.totalAnswers}
                    perMoment={responseSummary.perMoment}
                    loading={hierarchyLoading}
                    error={hierarchyError}
                />
            )}
            <RespondentOverlapNote overlap={respondentOverlap} />
            <SuspectSubmissionsNote suspect={suspectSubmissions} />
            {projectBreakdown
                ? <ProjectsBreakdownSection values={projectBreakdown} valueLabel="Gemiddelde" average />
                : <ProjectsBreakdownSection values={popoverRespondents} valueLabel="Antwoorden" />}
        </>
    );


    // Function to select visibility of graph or text or both for paragraph-moment questions
    const selectTextOrGraphVisibility = async (e) => {

        const selectedOption = e.currentTarget.dataset.option 

        await db.collection('QuestionnaireFields')
        .doc(docid)
        .update({
            ParagraphMomentVisibilityStyle: selectedOption 
        })

    }

    return (
        <div className="home-kpi-chart-container">
            {/* Rendered unconditionally (non-template) — it portals into the card
                header, so it must not depend on the title block, which is hidden
                for MSI-standard indicators. On a redesigned deep-analysis page
                the slots take over and the popover is suppressed entirely. */}
            {!templateData && !deepAnalysisSlots && (
                <CardHeaderInfoPopover label="Details" content={popoverContent} />
            )}
            {!templateData && deepAnalysisSlots?.factsEl &&
                createPortal(factsContent, deepAnalysisSlots.factsEl)}
            {!templateData && deepAnalysisSlots?.participationEl &&
                createPortal(participationContent, deepAnalysisSlots.participationEl)}
            {!inLiveReport && (indicator.Title || indicator.Standard !== 'true') && (
                <div className="home-kpi-description-container">
                    {templateData
                        ? (indicator.Standard !== 'true' && indicator.Question && <p>{indicator.Question}</p>)
                        : <QuestionMeta field={indicator.ID} />
                    }
                    {indicator.Title && <p>{indicator.Title}</p>}
                </div>
            )}

            {templateData ? (
                <TemplateIndicatorChart
                    fieldId={indicator.ID}
                    templateData={templateData}
                    graphType={graphType}
                    graphColor={graphColor}
                    colorPalette={colorPalette}
                    colorOverrides={colorOverrides}
                    numberFontSize={numberFontSize}
                    numberTextAlign={numberTextAlign}
                    numberFontWeight={numberFontWeight}
                />
            ) : (
                <DataType
                    effectId={effectId}
                    type={indicator.Type}
                    momentId={momentId}
                    fieldId={indicator.ID}
                    startDate={startDate}
                    endDate={endDate}
                    researchId={researchIdForDataType}
                    graphType={graphType}
                    graphColor={graphColor}
                    personaId={personaId}
                    paragrapgMomentVisibilityStyle={paragrapgMomentVisibilityStyle}
                    matrixQuestionRows={matrixQuestionRows}
                    projectId={indicator.CompagnyID}
                    colors={colors}
                    colorPalette={colorPalette}
                    colorOverrides={colorOverrides}
                    primaryColorOverride={overrides?.primary}
                    numberFontSize={numberFontSize}
                    numberTextAlign={numberTextAlign}
                    numberFontWeight={numberFontWeight}
                    filterByResearchId={filterByResearchId}
                    onProjectBreakdownChange={setProjectBreakdown}
                />
            )}

            {showWhenNote && (
                <p className="kpi-chart-showwhen-note">
                    Niet aan iedereen gesteld — alleen als {showWhenNote}
                </p>
            )}

            {dashboardOptions}


            {/* Paragraph-moment visibility style toggle */}
            <div
            id='parargraph-moment-visibility-style-option-container'
            style={{
                display: type === 'paragraph-moment'
                        ? 'flex' 
                        : 'none'
                }}
            >
                <Tooltip content={'Responses zichtbaarheid aanpassen'} width={'50px'} top='30px'>
                    <ChatOutlinedIcon onClick={() => setShowResponsesVisibilityOptions(!showResponsesVisibilityOptions)}/>
                </Tooltip>
                <div className='add-options-container live-report-graph-options-container' style={{display: showResponsesVisibilityOptions ? 'flex' : 'none'}} onMouseLeave={() => setShowResponsesVisibilityOptions(!showResponsesVisibilityOptions)}>
                   <p style={{color: paragrapgMomentVisibilityStyle === 'both' ? 'green' : 'black'}} data-option={'both'} onClick={selectTextOrGraphVisibility}>Reponses + grafiek</p>
                    <p style={{color: paragrapgMomentVisibilityStyle === 'text' ? 'green' : 'black'}} data-option={'text'} onClick={selectTextOrGraphVisibility}>Alleen reponses</p>
                    <p style={{color: paragrapgMomentVisibilityStyle === 'graph' ? 'green' : 'black'}} data-option={'graph'} onClick={selectTextOrGraphVisibility}>Alleen grafiek</p>
                </div>
            </div>
        </div>
    );
};

export default QuestionData;
